import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  ResultCategory,
  ResultSubcategory,
  ResultValue,
  SubcatGraphStyle,
  TimeHorizonKey,
} from '../../../../services/result-data/result-data.service';
import {
  CardComponent,
  CardConfig,
  DataPointCardConfig,
  PieChartCardConfig,
  PieChartCardSlice,
  TimeSeriesCardConfig,
  TimeSeriesCardDataset,
  TitleCardConfig,
} from '../../card/card.component';
import {
  resolveCardColspan,
  resolveCardLabel,
  resolveCardTitle,
} from '../../card/card.utils';
import {
  TimeHorizonConfig,
  TimeHorizonService,
} from '../../../../services/time-horizon/time-horizon.service';
import {
  DEFAULT_CARD_SUBTITLE,
  DEFAULT_CARD_TITLE,
  DEFAULT_DATA_POINT_DOUBLE_COLSPAN,
  DEFAULT_DATA_POINT_SINGLE_COLSPAN,
  DEFAULT_PIE_CHART_COLSPAN,
  DEFAULT_TIME_SERIES_COLSPAN,
  DEFAULT_TITLE_CARD_COLSPAN,
} from '../../../../constants';

@Component({
  selector: 'app-result-category-view',
  imports: [CardComponent],
  templateUrl: './result-category-view.component.html',
  styleUrl: './result-category-view.component.scss',
})
export class ResultCategoryViewComponent implements OnChanges {
  @Input() category!: ResultCategory;
  @Input() timeHorizon: TimeHorizonKey = '';

  cardConfigsBySubcat: CardConfig[][] = [];

  constructor(private readonly _timeHorizonService: TimeHorizonService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['category'] || changes['timeHorizon']) {
      this._generateCardConfigsFromCategory();
    }
  }

  private _getTimeHorizonConfig(): TimeHorizonConfig {
    return this._timeHorizonService.getConfig(this.timeHorizon);
  }

  private _buildTimeSeriesDatasets(
    subcategoryValues: ResultValue[],
    yearKeys: (keyof ResultValue)[]
  ): TimeSeriesCardDataset[] {
    const datasets: TimeSeriesCardDataset[] = [];

    const toDataset = (
      value: ResultValue,
      variant: TimeSeriesCardDataset['variant']
    ): TimeSeriesCardDataset => ({
      label: value.name,
      values: yearKeys.map((key) => Number(value[key] ?? 0)),
      variant,
    });

    for (const value of subcategoryValues ?? []) {
      datasets.push(toDataset(value, 'primary'));
      for (const child of value.subValues ?? []) {
        datasets.push(toDataset(child, 'breakdown'));
      }
    }

    return datasets;
  }

  private _getUnitFromValues(values: ResultValue[]): string | undefined {
    for (const value of values) {
      if (value.unit) return value.unit;
      const nestedWithUnit = (value.subValues ?? []).find((sv) => sv.unit);
      if (nestedWithUnit?.unit) return nestedWithUnit.unit;
    }
    return undefined;
  }

  private _generateCardConfigsFromCategory() {
    if (!this.category) {
      this.cardConfigsBySubcat = [];
      return;
    }

    const horizonConfig = this._getTimeHorizonConfig();
    const allowedYearKeys: (keyof ResultValue)[] = [
      'y1',
      'y2',
      'y3',
      'y4',
      'y5',
    ];
    const yearKeys = horizonConfig.yearKeys.filter(
      (key): key is keyof ResultValue =>
        (allowedYearKeys as string[]).includes(key)
    );
    const labels = horizonConfig.labels.slice(0, yearKeys.length);

    this.cardConfigsBySubcat = [];

    const sanitizedCategoryName = this.category.name.replace(
      /[^a-zA-Z0-9_-]/g,
      '_'
    );

    for (let i = 0; i < this.category.subcategories.length; i++) {
      const subcatConfigs: CardConfig[] = [];

      const subcat = this.category.subcategories[i];

      const sanitizedSubcatName = subcat.name.replace(/[^a-zA-Z0-9_-]/g, '_');
      const transitionIdBase = `card-${sanitizedCategoryName}-${sanitizedSubcatName}-${i}`;

      const titleCard = this._generateTitleCard(
        subcat,
        i,
        `${transitionIdBase}-title`
      );
      const dataPointCards = this._generateDataPointCards(
        subcat.values ?? [],
        i,
        `${transitionIdBase}-data`
      );
      const timeSeriesCards = this._generateTimeSeriesCards(
        subcat.values ?? [],
        yearKeys,
        labels,
        i,
        `${transitionIdBase}-time-series`
      );
      const pieChartCards = this._generatePieChartCards(
        subcat.values,
        yearKeys,
        labels,
        i,
        `${transitionIdBase}-pie`
      );

      subcatConfigs.push(
        titleCard,
        ...timeSeriesCards,
        ...dataPointCards,
        ...pieChartCards
      );
      subcatConfigs.sort((a, b) => a.cardIndex - b.cardIndex);

      this.cardConfigsBySubcat.push(subcatConfigs);
    }
  }

  private _generateTitleCard(
    subcat: ResultSubcategory,
    subcatIndex: number,
    transitionId: string
  ): TitleCardConfig {
    const config: TitleCardConfig = {
      transitionId,
      title: subcat.name,
      type: 'title',
      imgFileName: subcat.titleCardImgFileName,
      categoryName: this.category.name,
      colorIndex: subcatIndex % 4,
      cardIndex: 0,
      accent: true,
      colspan: resolveCardColspan(
        subcat.titleCardColspan,
        DEFAULT_TITLE_CARD_COLSPAN
      ),
      rowspan: subcat.titleCardRowspan ?? 2,
    };

    return config;
  }

  private _generateTimeSeriesCards(
    values: ResultValue[],
    yearKeys: (keyof ResultValue)[],
    labels: string[],
    subcatIndex: number,
    transitionIdBase: string
  ): TimeSeriesCardConfig[] {
    const configs: TimeSeriesCardConfig[] = [];

    values.forEach((value, index) => {
      const nestedTransitionBase = `${transitionIdBase}-${index}`;
      const timeSeriesMeta = value.timeSeries;
      const hasGraphIndex =
        typeof timeSeriesMeta?.index === 'number' &&
        Number.isFinite(timeSeriesMeta.index);

      if (hasGraphIndex) {
        const datasets = this._buildTimeSeriesDatasets(
          [value],
          yearKeys
        );
        const unit = this._getUnitFromValues([
          value,
          ...(value.subValues ?? []),
        ]);
        const chartStyle: SubcatGraphStyle = timeSeriesMeta?.style ?? 'bar';
        const title = resolveCardTitle(
          timeSeriesMeta?.title,
          DEFAULT_CARD_TITLE
        );

        configs.push({
          transitionId: `${nestedTransitionBase}-card`,
          title,
          type: 'timeSeries',
          info: timeSeriesMeta?.info,
          categoryName: this.category.name,
          colorIndex: subcatIndex % 4,
          cardIndex: timeSeriesMeta.index!,
          accent: false,
          chartStyle,
          labels: [...labels],
          datasets,
          unit,
          colspan: resolveCardColspan(
            timeSeriesMeta?.colspan,
            DEFAULT_TIME_SERIES_COLSPAN
          ),
        });
      }

      if (value.subValues?.length) {
        configs.push(
          ...this._generateTimeSeriesCards(
            value.subValues,
            yearKeys,
            labels,
            subcatIndex,
            nestedTransitionBase
          )
        );
      }
    });

    return configs;
  }

  private _generateDataPointCards(
    values: ResultValue[],
    subcatIndex: number,
    transitionIdBase: string
  ): DataPointCardConfig[] {
    const configs: DataPointCardConfig[] = [];

    for (let i = 0; i < values.length; i++) {
      const val = values[i];
      const dataPointMeta = val.dataPoint;
      const cardTransitionId = `${transitionIdBase}-${i}`;
      const hasDataPointIndex =
        typeof dataPointMeta?.index === 'number' &&
        Number.isFinite(dataPointMeta.index);

      if (hasDataPointIndex) {
        const config: DataPointCardConfig = {
          transitionId: cardTransitionId,
          title: resolveCardTitle(dataPointMeta?.title, DEFAULT_CARD_TITLE),
          type: 'dataPoint',
          dataPointCardType: 'single',
          imgFileName: dataPointMeta?.imgFileName,
          info: dataPointMeta?.info,
          categoryName: this.category.name,
          colorIndex: subcatIndex % 4,
          cardIndex: dataPointMeta.index!,
          accent: false,
          colspan: this._resolveResultValueColspan(val, 'single'),
          data: [
            {
              label: resolveCardLabel(
                dataPointMeta?.subtitle,
                DEFAULT_CARD_SUBTITLE
              ),
              value: val.y5,
              unit: val.unit,
            },
          ],
        };

        configs.push(config);
      }

      if (val.subValues && val.subValues.length > 0) {
        const subValConfigs = this._generateDataPointCards(
          val.subValues ?? [],
          subcatIndex,
          `${cardTransitionId}-child`
        );
        configs.push(...subValConfigs);
      }
    }
    return configs;
  }

  private _resolveResultValueColspan(
    value: ResultValue,
    cardType: 'single' | 'double'
  ): number {
    const dataPointMeta = value.dataPoint;
    const fallback =
      cardType === 'double'
        ? DEFAULT_DATA_POINT_DOUBLE_COLSPAN
        : DEFAULT_DATA_POINT_SINGLE_COLSPAN;
    return resolveCardColspan(dataPointMeta?.colspan, fallback);
  }

  private _generatePieChartCards(
    values: ResultValue[] | undefined,
    yearKeys: (keyof ResultValue)[],
    labels: string[],
    subcatIndex: number,
    transitionIdBase: string
  ): PieChartCardConfig[] {
    if (!values?.length) {
      return [];
    }

    const { yearKey, yearLabel } = this._resolvePieChartYear(yearKeys, labels);
    const horizonYears = this._getTimeHorizonConfig().number;
    const configs: PieChartCardConfig[] = [];

    values.forEach((value, index) => {
      const nestedTransitionBase = `${transitionIdBase}-${index}`;
      const pieChartMeta = value.pieChart;
      const hasPieChartIndex =
        typeof pieChartMeta?.index === 'number' &&
        Number.isFinite(pieChartMeta.index);

      if (hasPieChartIndex && value.subValues && value.subValues.length > 0) {
        const slices = this._buildPieChartSlices(value.subValues, yearKey);
        const total = slices.reduce((sum, slice) => sum + slice.value, 0);

        if (total > 0) {
          configs.push({
            transitionId: `${nestedTransitionBase}-card`,
            title: resolveCardTitle(pieChartMeta?.title, DEFAULT_CARD_TITLE),
            subtitle: pieChartMeta?.subtitle?.toString().trim() || undefined,
            type: 'pieChart',
            categoryName: this.category.name,
            colorIndex: subcatIndex % 4,
            cardIndex: pieChartMeta.index!,
            accent: false,
            colspan: resolveCardColspan(
              pieChartMeta?.colspan,
              DEFAULT_PIE_CHART_COLSPAN
            ),
            slices,
            total,
            unit: this._getUnitFromValues(value.subValues ?? []),
            info: pieChartMeta?.info,
            yearLabel,
            timeHorizonYears: horizonYears,
          });
        }
      }

      if (value.subValues?.length) {
        configs.push(
          ...this._generatePieChartCards(
            value.subValues,
            yearKeys,
            labels,
            subcatIndex,
            nestedTransitionBase
          )
        );
      }
    });

    return configs;
  }

  private _buildPieChartSlices(
    values: ResultValue[],
    yearKey: keyof ResultValue
  ): PieChartCardSlice[] {
    return values.map((child) => {
      const rawValue = Number(child[yearKey] ?? 0);
      const numericValue = Number.isFinite(rawValue) ? rawValue : 0;

      return {
        label: child.name,
        value: Math.abs(numericValue),
        rawValue: numericValue,
      };
    });
  }

  private _resolvePieChartYear(
    yearKeys: (keyof ResultValue)[],
    labels: string[]
  ): { yearKey: keyof ResultValue; yearLabel: string } {
    if (yearKeys.length > 0) {
      const index = yearKeys.length - 1;
      const fallbackLabel =
        this._getTimeHorizonConfig().labels.at(-1) ??
        'Année ' + this._getTimeHorizonConfig().number;

      return {
        yearKey: yearKeys[index],
        yearLabel: labels[index] ?? fallbackLabel,
      };
    }

    const fallbackConfig = this._getTimeHorizonConfig();
    const fallbackYearKey = 'y5' as keyof ResultValue;
    const fallbackLabel =
      labels.at(-1) ??
      fallbackConfig.labels.at(-1) ??
      'Année ' + fallbackConfig.number;

    return {
      yearKey: fallbackYearKey,
      yearLabel: fallbackLabel,
    };
  }
}
